import React, { useEffect, useMemo, useRef } from 'react'
import { Button, Flex } from 'antd'
import { ColDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Company } from '../../../../types/company/Company'
import { SiblingDecision } from '../../utils/buildSiblingDecision'
import { SiblingCandidates } from '../../utils/groupSuggestionsBySibling'
import { mappingGridTheme, MAPPING_GRID_HEADER_HEIGHT } from '../../utils/mappingGridTheme'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import StepHeading from './StepHeading'
import CellSignalLegend from './CellSignalLegend'
import { DecisionCellRenderer, WizardConfirmGridContext } from './wizardConfirmCellRenderers'

const recordCount = (decision: SiblingDecision | undefined) =>
  (decision?.forwardCustomer.length ?? 0) + (decision?.forwardSupplier.length ?? 0)

interface WizardStepConfirmProps {
  target: Company
  selectedSiblings: Company[]
  candidatesBySibling: Map<string, SiblingCandidates>
  decisions: Map<string, SiblingDecision>
  committing: boolean
  onBack: () => void
  onCommit: () => void
}

const WizardStepConfirm: React.FC<WizardStepConfirmProps> = ({
  target,
  selectedSiblings,
  candidatesBySibling,
  decisions,
  committing,
  onBack,
  onCommit
}) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact>(null)

  // Decision cells read from grid context, which ag-grid does not re-render on its own; force a
  // refresh (and auto-height re-measure) whenever the resolved decisions or candidates change.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true })
    gridRef.current?.api?.resetRowHeights()
  }, [candidatesBySibling, decisions])

  const totalCreated = selectedSiblings.reduce((sum, sibling) => sum + recordCount(decisions.get(sibling.id)), 0)

  const columnDefs = useMemo<ColDef<Company>[]>(
    () => [
      {
        colId: 'sibling',
        headerName: t('groupCustomerMapping:wizardConfirmSibling', 'Sisaryhtiö'),
        field: 'name',
        flex: 1
      },
      {
        colId: 'customer',
        headerName: t('groupCustomerMapping:wizardConfirmCustomer', 'Asiakkaana'),
        flex: 2,
        autoHeight: true,
        cellRenderer: DecisionCellRenderer,
        cellRendererParams: { role: 'customer' }
      },
      {
        colId: 'supplier',
        headerName: t('groupCustomerMapping:wizardConfirmSupplier', 'Toimittajana'),
        flex: 2,
        autoHeight: true,
        cellRenderer: DecisionCellRenderer,
        cellRendererParams: { role: 'supplier' }
      },
      {
        colId: 'records',
        headerName: t('groupCustomerMapping:wizardConfirmRecords', 'Tietueet'),
        width: 100,
        valueGetter: params => (params.data ? recordCount(decisions.get(params.data.id)) : 0)
      }
    ],
    [t, decisions]
  )

  const gridContext = useMemo<WizardConfirmGridContext>(
    () => ({ targetId: target.id, candidatesBySibling, decisions }),
    [target.id, candidatesBySibling, decisions]
  )

  return (
    <>
      <StepHeading
        title={t('groupCustomerMapping:wizardConfirmTitle', 'Yhteenveto · {{target}}', { target: target.name })}
        description={t(
          'groupCustomerMapping:wizardConfirmDescription',
          'Tarkista ennen kirjausta. Kaikki kirjataan kerralla.'
        )}
      />

      <CellSignalLegend />

      <div style={{ marginBottom: 16 }}>
        <CustomAgGrid
          ref={gridRef}
          theme={mappingGridTheme}
          domLayout="autoHeight"
          columnDefs={columnDefs}
          rowData={selectedSiblings}
          context={gridContext}
          getRowId={params => params.data.id}
          headerHeight={MAPPING_GRID_HEADER_HEIGHT}
        />
      </div>

      <Flex justify="space-between">
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          {t('groupCustomerMapping:wizardConfirmBack', 'Takaisin')}
        </Button>
        <Button type="primary" onClick={onCommit} loading={committing} disabled={totalCreated === 0}>
          {t('groupCustomerMapping:wizardConfirmCommit', 'Kirjaa {{count}} linkitystä', { count: totalCreated })}
        </Button>
      </Flex>
    </>
  )
}

export default WizardStepConfirm
